/**
 * BirthdayRemindersPanel — Communications ▸ Birthday Reminders (AJ, 2026-10-12).
 * Replaces the "Birthday Reminder" Google Sheet. Condensed, inline-edit, saves
 * on change (no pencil/modal). Data = the Family Directory players' birthdays
 * (shared dataset) + coaches from the AD Coaches configurator.
 * See docs/BIRTHDAY_REMINDERS.md.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Send, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/birthday-reminders`;
const inp = 'h-7 text-xs rounded border border-slate-300 px-2 bg-white text-slate-900';
const fmt = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export default function BirthdayRemindersPanel({ orgId }) {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [log, setLog] = useState(null);
  const loadLog = async () => {
    try { const r = await axios.get(`${API}/${orgId}/log`); setLog(r.data); } catch { toast.error('Could not load the send log'); }
  };

  const load = useCallback(async () => {
    if (!orgId) return;
    try { const r = await axios.get(`${API}/${orgId}`); setData(r.data); setCfg(r.data.config); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load birthday reminders'); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch, reload = false) => {
    try {
      const r = await axios.put(`${API}/${orgId}`, patch);
      setCfg((c) => ({ ...c, ...r.data }));
      if (reload) load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); load(); }
  };
  const setLocal = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const sportSave = (code, patch) => {
    setCfg((c) => ({ ...c, sports: { ...c.sports, [code]: { ...c.sports[code], ...patch } } }));
    save({ sports: { [code]: patch } }, true);
  };
  const recips = cfg?.recipients || [];
  const saveRecips = (list) => { setLocal({ recipients: list }); save({ recipients: list.filter((r) => r.email || r.name) }); };
  const editRecip = (i, patch) => setLocal({ recipients: recips.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  const sendTest = async () => {
    setTesting(true);
    try { const r = await axios.post(`${API}/${orgId}/test`, { email: testEmail }); toast.success(`Test sent (rendered for ${r.data.rendered_for})`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); } finally { setTesting(false); }
  };

  if (!data || !cfg) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-3 text-sm" data-testid="birthday-reminders">
      {/* Master switch */}
      <div className={`flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 ${cfg.enabled ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
        <Switch checked={cfg.enabled} onCheckedChange={(v) => { setLocal({ enabled: v }); save({ enabled: v }); }} data-testid="bd-master" />
        <span className="font-semibold text-slate-800">{cfg.enabled ? 'OL is sending birthday reminders' : 'Birthday reminders are OFF'}</span>
        <label className="flex items-center gap-1 text-xs text-slate-600">daily at
          <input type="time" className={inp} value={cfg.send_time} onChange={(e) => setLocal({ send_time: e.target.value })} onBlur={() => save({ send_time: cfg.send_time })} data-testid="bd-time" />
        </label>
        <div className="ml-auto flex items-center gap-1">
          <input className={`${inp} w-48`} placeholder="Send a test to…" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="bd-test-email" />
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={testing || !testEmail} onClick={sendTest} data-testid="bd-test-send"><Send className="h-3 w-3 mr-1" />Test</Button>
        </div>
      </div>

      {/* Message + recipients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Message</h4>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-slate-500">From name
              <input className={`${inp} w-full`} value={cfg.from_name} placeholder={cfg.default_from_name || '(site default)'} onChange={(e) => setLocal({ from_name: e.target.value })} onBlur={() => save({ from_name: cfg.from_name })} data-testid="bd-from-name" /></label>
            <label className="text-[11px] text-slate-500">Reply-to
              <input className={`${inp} w-full`} value={cfg.reply_to} placeholder="(none)" onChange={(e) => setLocal({ reply_to: e.target.value })} onBlur={() => save({ reply_to: cfg.reply_to })} data-testid="bd-reply-to" /></label>
          </div>
          <p className="text-[10px] text-slate-400">Sent from the website's mail address (wp_mail).</p>
          <label className="text-[11px] text-slate-500 block">Subject
            <input className={`${inp} w-full`} value={cfg.subject} onChange={(e) => setLocal({ subject: e.target.value })} onBlur={() => save({ subject: cfg.subject })} data-testid="bd-subject" /></label>
          <label className="text-[11px] text-slate-500 block">Message
            <textarea className="w-full text-xs rounded border border-slate-300 p-2 bg-white text-slate-900" rows={3} value={cfg.body} onChange={(e) => setLocal({ body: e.target.value })} onBlur={() => save({ body: cfg.body })} data-testid="bd-body" /></label>
          <p className="text-[10px] text-slate-400">Wildcards: {(data.wildcards || []).map((w) => `#${w}#`).join(' ')}</p>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Send to</h4>
          <div className="flex gap-4 text-xs text-slate-700">
            <label className="flex items-center gap-1"><input type="checkbox" checked={cfg.include_head_coaches} onChange={(e) => { setLocal({ include_head_coaches: e.target.checked }); save({ include_head_coaches: e.target.checked }, true); }} data-testid="bd-hc" />Player's head coach(es)</label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={cfg.include_assistant_coaches} onChange={(e) => { setLocal({ include_assistant_coaches: e.target.checked }); save({ include_assistant_coaches: e.target.checked }, true); }} data-testid="bd-ac" />Assistant coaches</label>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-slate-400"><th className="py-1">On</th><th>Name</th><th>Email</th><th /></tr></thead>
            <tbody>
              {recips.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1"><input type="checkbox" checked={r.active !== false} onChange={(e) => saveRecips(recips.map((x, j) => (j === i ? { ...x, active: e.target.checked } : x)))} /></td>
                  <td><input className={`${inp} w-full`} value={r.name} placeholder="e.g. Athletic Director" onChange={(e) => editRecip(i, { name: e.target.value })} onBlur={() => saveRecips(recips)} /></td>
                  <td><input className={`${inp} w-full`} value={r.email} placeholder="name@example.org" onChange={(e) => editRecip(i, { email: e.target.value })} onBlur={() => saveRecips(recips)} data-testid={`bd-recip-email-${i}`} /></td>
                  <td className="text-right"><button className="text-slate-400 hover:text-red-500" onClick={() => saveRecips(recips.filter((_, j) => j !== i))} title="Remove"><Trash2 className="h-3.5 w-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setLocal({ recipients: [...recips, { name: '', email: '', active: true }] })} data-testid="bd-recip-add"><Plus className="h-3 w-3 mr-1" />Add recipient</Button>
        </div>
      </div>

      {/* Send log (last 60 days) */}
      <div className="rounded-lg border border-slate-200 bg-white">
        <button className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500"
          onClick={() => (log ? setLog(null) : loadLog())} data-testid="bd-log-toggle">
          <span>Sent log (last 60 days){cfg.last_run_local_date ? ` · last run ${cfg.last_run_local_date}` : ''}</span><span>{log ? 'Hide' : 'Show'}</span>
        </button>
        {log && (log.rows.length === 0
          ? <p className="px-3 pb-2 text-xs text-slate-500">No reminders sent in the last {log.days} days.</p>
          : (
            <div className="max-h-56 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-[10px] uppercase text-slate-400">
                  <th className="px-3 py-1">Date</th><th>Player</th><th>Team</th><th>Sent to</th></tr></thead>
                <tbody>
                  {log.rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-3 py-0.5 whitespace-nowrap">{r.date}</td><td>{r.player}</td><td className="text-slate-600">{r.team_slug}</td>
                      <td className="text-slate-600">{(r.sent || []).map((x) => `${x.to}${x.status && x.status !== 'sent' ? ` (${x.status})` : ''}`).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </div>

      {/* One condensed table per sport */}
      {data.sports.map((sp) => {
        const sc = cfg.sports?.[sp.code] || {};
        const noEmail = sp.teams.filter((t) => t.coaches.length === 0 || t.coaches.some((c) => !c.email));
        return (
          <div key={sp.code} className="rounded-lg border border-slate-200 bg-white" data-testid={`bd-sport-${sp.code}`}>
            <div className="flex flex-wrap items-center gap-3 px-3 py-2 border-b border-slate-100">
              <Switch checked={!!sc.enabled} onCheckedChange={(v) => sportSave(sp.code, { enabled: v })} data-testid={`bd-sport-on-${sp.code}`} />
              <span className="font-semibold text-slate-800">{sp.name}</span>
              <span className="text-xs text-slate-500">{sp.season_year ? `${sp.season_year} season · last game ${fmt(sp.last_game)}${sp.season_over ? ' (season over)' : ''}` : 'No current-season players'}</span>
              <label className="ml-auto flex items-center gap-1 text-xs text-slate-600">
                <input type="checkbox" checked={!!sc.stop_at_season_end} onChange={(e) => sportSave(sp.code, { stop_at_season_end: e.target.checked })} data-testid={`bd-stop-${sp.code}`} />
                Turn off after last game
              </label>
            </div>
            {sc.auto_disabled_at && !sc.enabled && (
              <p className="px-3 py-1 text-[11px] text-amber-700 bg-amber-50">Turned off automatically after the season's last game ({fmt(sc.auto_disabled_at.slice(0, 10))}). Turn it on any time.</p>
            )}
            {noEmail.length > 0 && (
              <p className="px-3 py-1 text-[11px] text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />
                No coach email for: {noEmail.map((t) => t.label).join(', ')} — set it in Coaches.</p>
            )}
            {sp.players.length > 0 && (
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-[10px] uppercase text-slate-400">
                    <th className="px-3 py-1">Player</th><th>Team</th><th>Birthday</th><th>Turns</th><th>Next</th><th>Coach</th></tr></thead>
                  <tbody>
                    {sp.players.map((p) => {
                      const team = sp.teams.find((t) => t.slug === p.team_slug) || { coaches: [] };
                      return (
                        <tr key={`${p.player_id}-${p.team_slug}`} className={`border-t border-slate-100 ${p.today ? 'bg-pink-50 font-semibold' : ''}`}>
                          <td className="px-3 py-0.5">{p.name}</td>
                          <td className="text-slate-600">{p.team}</td>
                          <td>{fmt(p.birthday)}</td>
                          <td>{p.turning}</td>
                          <td>{p.today ? 'Today 🎂' : `${p.days_until}d`}</td>
                          <td className="text-slate-600">{team.coaches.map((c) => c.name).join(', ') || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
